import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject,
  input,
  output,
} from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { finalize } from 'rxjs/operators'
import { environment } from '../../../../environments/environment'
import { FeatureFlag } from '../../../constants/feature-flags'
import {
  HubtypeProviderAccount,
  WhatsappPhoneNumber,
} from '../../../models/hubtype-provider-account'
import { OrganizationService } from '../../../services/hubtype-api/organization.service'
import { ProviderAccountService } from '../../../services/hubtype-api/provider-account.service'
import { LoggerService } from '../../../services/logger.service'
import { AngularComponent } from '../../AngularComponent'
import {
  InitParams,
  LoginOptions,
} from '../../add-channels/facebook/models/facebook.models'
import { FacebookService } from '../../add-channels/facebook/services/facebook.service'

@Component({
  selector: 'app-channels-whatsapp',
  templateUrl: './whatsapp.component.html',
  styleUrls: ['./whatsapp.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class WhatsappComponent extends AngularComponent implements OnInit {
  readonly #providerAccountService = inject(ProviderAccountService)
  readonly #ref = inject(ChangeDetectorRef)
  readonly #facebookService = inject(FacebookService)
  readonly #organizationService = inject(OrganizationService)
  readonly #logger = inject(LoggerService)
  readonly #destroyRef = inject(DestroyRef)

  readonly provider = input.required<string>()
  readonly newChannel = output<HubtypeProviderAccount>()

  readonly showCoexistenceButton = toSignal(
    this.#organizationService.hasFeatureFlag(
      FeatureFlag.WHATSAPP_BUSINESS_APP_COEXISTENCE
    )
  )

  phoneNumbers: WhatsappPhoneNumber[]
  selectedPhoneNumber: WhatsappPhoneNumber
  loadingPhoneNumbers = false
  feedback = false
  isCoexistenceMode = false
  noPhoneNumbersFeedback = false

  constructor() {
    super()
  }

  ngOnInit(): void {
    const initParams: InitParams = {
      appId: environment.facebook_app_id,
      xfbml: true,
      version: environment.facebook_graph_api_version,
    }
    this.#facebookService.init(initParams).catch(() => {
      this.feedback = true
    })
  }

  launchWhatsAppSignup() {
    this.isCoexistenceMode = false
    this.launchSignup({
      version: 'v4',
      sessionInfoVersion: '4',
      // v4: features array (e.g. marketing_messages_lite, app_only_install)
      // is no longer passed here — the config_id itself defines which
      // products are enabled in the onboarding flow.
    })
  }

  launchWhatsAppCoexistenceSignup() {
    this.isCoexistenceMode = true
    this.launchSignup({
      version: 'v4',
      feature_type: 'whatsapp_business_app_onboarding',
      sessionInfoVersion: '4',
    })
  }

  getPhoneNumbers(code: string) {
    this.feedback = false
    this.loadingPhoneNumbers = true
    this.noPhoneNumbersFeedback = false

    this.#providerAccountService
      .getWhatsappPhoneNumbers(this.provider(), code)
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        finalize(() => {
          this.loadingPhoneNumbers = false
          this.#ref.markForCheck()
        })
      )
      .subscribe({
        next: phoneNumbers => {
          if (phoneNumbers && phoneNumbers.length > 0) {
            this.phoneNumbers = phoneNumbers
            this.selectedPhoneNumber = this.phoneNumbers[0]
          } else {
            this.phoneNumbers = phoneNumbers ?? []
            this.noPhoneNumbersFeedback = true
          }
        },
        error: () => {
          this.feedback = true
        },
      })
  }

  addWhatsappCloud() {
    const provider = new HubtypeProviderAccount()
    provider.provider = this.provider()
    provider.credentials_json = {
      waba_id: this.selectedPhoneNumber.waba_id,
      phone_number_id: this.selectedPhoneNumber.id,
      display_phone_name: this.selectedPhoneNumber.display_phone_name,
      display_phone_number: this.selectedPhoneNumber.display_phone_number,
      is_business_app_coexistence: this.isCoexistenceMode,
    }
    this.newChannel.emit(provider)
  }

  private launchSignup(extras: Record<string, unknown>) {
    const configId = environment.facebook_signup_config_id
    if (!configId || configId.startsWith('WHATSAPP_ES_CONFIG_ID')) {
      this.feedback = true
      const reason = !configId
        ? 'facebook_signup_config_id is not set in this environment'
        : 'facebook_signup_config_id is a placeholder — replace with a real Meta config ID'
      this.#logger.logRegularError('WhatsAppEmbeddedSignup', reason, {
        extra: { config_id: configId },
      })
      console.error(reason)
      return
    }

    const loginOptions: LoginOptions = {
      config_id: configId,
      response_type: 'code',
      override_default_response_type: true,
      extras,
    }
    this.#facebookService
      .login(loginOptions)
      .then(response => {
        const code = response.authResponse?.code
        if (code) {
          this.getPhoneNumbers(code)
        } else {
          console.warn('Embedded signup returned no auth code')
          this.feedback = true
        }
      })
      .catch(error => {
        this.feedback = true
        if (error) {
          this.#logger.logRegularError(
            'WhatsAppEmbeddedSignup',
            'Embedded signup failed',
            { extra: { error: String(error) } }
          )
        }
        console.error('Embedded signup failed', error)
      })
  }
}
